import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetOpsHealth,
  GetOpsLobby,
  GetOpsDoctors,
  GetOpsPrescriptions,
  ResolveOpsPrescription,
  GetOpsInsights,
  GetOpsAnalytics,
  GetOpsUsersPatients,
  GetOpsUsersProviders,
  GetOpsStateCoverage,
  GetOpsDoctorCoverage,
  GetOpsAlerts,
  GetOpsAlertDetail,
} from "Services/API/Operations.service";
/**
 * React-query hooks for the Operations dashboard, one per endpoint (same
 * pattern as CustomHooks/useConversions). Each takes `enabled` (so a tab only
 * fetches when active) and the `forceRef` used to bypass any cache on Refresh,
 * and returns the raw query result so the dashboard can drive its own
 * loading/error/skeleton composition.
 *
 * No auto-polling: queries fetch on mount (when a tab opens) and on the manual
 * Refresh button only — nothing reloads on a timer.
 */
const opts = (enabled) => ({
  enabled,
  refetchInterval: false,
  keepPreviousData: true,
});

export const useOpsLobby = ({ range, enabled, forceRef }) =>
  useQuery(
    ["opsLobby", range],
    () => GetOpsLobby({ range, force: forceRef.current }),
    opts(enabled)
  );

export const useOpsDoctors = ({ range, enabled, forceRef }) =>
  useQuery(
    ["opsDoctors", range],
    () => GetOpsDoctors({ range, force: forceRef.current }),
    opts(enabled)
  );

export const useOpsAnalytics = ({ range, enabled, forceRef }) =>
  useQuery(
    ["opsAnalytics", range],
    () => GetOpsAnalytics({ range, force: forceRef.current }),
    opts(enabled)
  );

export const useOpsInsights = ({ range, enabled, forceRef }) =>
  useQuery(
    ["opsInsights", range],
    () => GetOpsInsights({ range, force: forceRef.current }),
    opts(enabled)
  );

export const useOpsPrescriptions = ({ range, page, resolvedPage, enabled, forceRef }) =>
  useQuery(
    ["opsPrescriptions", range, page, resolvedPage],
    () => GetOpsPrescriptions({ range, page, resolvedPage, force: forceRef.current }),
    opts(enabled)
  );

// Marks a worklist prescription resolved; refreshes the list so the item moves
// from Needs Attention to Resolved.
export const useResolveOpsPrescription = () => {
  const queryClient = useQueryClient();
  return useMutation((vars) => ResolveOpsPrescription(vars), {
    onSuccess: () => queryClient.invalidateQueries(["opsPrescriptions"]),
  });
};

export const useOpsUsersPatients = ({ range, page, sort, search, enabled, forceRef }) =>
  useQuery(
    ["opsUsersPatients", range, page, sort.key, sort.order, search],
    () => GetOpsUsersPatients({ range, page, sort: sort.key, order: sort.order, search, force: forceRef.current }),
    opts(enabled)
  );

export const useOpsUsersProviders = ({ range, page, sort, search, enabled, forceRef }) =>
  useQuery(
    ["opsUsersProviders", range, page, sort.key, sort.order, search],
    () => GetOpsUsersProviders({ range, page, sort: sort.key, order: sort.order, search, force: forceRef.current }),
    opts(enabled)
  );

// State Coverage board for the Doctors tab (range-scoped).
export const useOpsStateCoverage = ({ range, enabled, forceRef }) =>
  useQuery(
    ["opsStateCoverage", range],
    () => GetOpsStateCoverage({ range, force: forceRef.current }),
    opts(enabled)
  );

// One doctor's per-state leave-impact breakdown, fetched only when a row is
// expanded (enabled + a real doctorId). Cached per doctor+range.
export const useOpsDoctorCoverage = ({ doctorId, range, enabled }) =>
  useQuery(
    ["opsDoctorCoverage", doctorId, range],
    () => GetOpsDoctorCoverage({ doctorId, range }),
    { enabled: enabled && !!doctorId, keepPreviousData: false }
  );

export const useOpsHealth = ({ enabled, forceRef }) =>
  useQuery(["opsHealth"], () => GetOpsHealth({ force: forceRef.current }), opts(enabled));

// Alert summary counts drive the triage cards + the tab badge. Kept enabled
// across tabs (cheap count query) so the badge shows without opening Alerts.
export const useOpsAlerts = ({ enabled, forceRef }) =>
  useQuery(["opsAlerts"], () => GetOpsAlerts({ force: forceRef.current }), opts(enabled));

// One alert's paginated list — only fires once a card is selected (real key).
export const useOpsAlertDetail = ({ key, page, enabled, forceRef }) =>
  useQuery(
    ["opsAlertDetail", key, page],
    () => GetOpsAlertDetail({ key, page, force: forceRef.current }),
    { ...opts(enabled && !!key) }
  );
